import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Heart, Pin, ShieldCheck, EyeOff, Eye, Trash2 } from "lucide-react";
import {
  getThread, createReply, likeReply, hideReply, deleteReply, apiError,
} from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import HumanCheck from "@/components/HumanCheck";

const likedKey = "arw_liked_replies";
const getLiked = () => JSON.parse(localStorage.getItem(likedKey) || "[]");
const addLiked = (id) => localStorage.setItem(likedKey, JSON.stringify([...getLiked(), id]));

function fmt(iso) {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function CommunityThread() {
  const { id } = useParams();
  const { isAdmin } = useAdmin();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getThread(id));
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (notFound) return (
    <div className="pt-40 pb-40 text-center" data-testid="thread-not-found">
      <p className="text-zinc-400">Discussion not found.</p>
      <Link to="/community" className="text-amber-500 mt-4 inline-block">Back to the community</Link>
    </div>
  );
  if (!data) return <div className="pt-40 pb-40 text-center text-zinc-600">Loading…</div>;

  const { post, replies } = data;

  const onLike = async (rid) => {
    if (getLiked().includes(rid)) return;
    try {
      const r = await likeReply(rid);
      addLiked(rid);
      setData((d) => ({ ...d, replies: d.replies.map((x) => x.id === rid ? { ...x, likes: r.likes } : x) }));
    } catch { /* noop */ }
  };

  const modReply = async (fn, rid, msg) => {
    try { await fn(rid); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="community-thread-page" className="pt-32">
      <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
        <Link to="/community" data-testid="back-to-community" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-500 transition-colors mb-10">
          <ArrowLeft size={16} /> The Gathering
        </Link>

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {post.pinned && <span className="inline-flex items-center gap-1 text-[10px] tracking-accent uppercase text-amber-500"><Pin size={12} /> Pinned</span>}
          <span className="text-[10px] tracking-accent uppercase text-zinc-500 border border-zinc-700 px-2 py-0.5">{post.category.replace(/-/g, " ")}</span>
        </div>
        <h1 data-testid="thread-title" className="font-display text-4xl font-black tracking-tight">{post.title}</h1>
        <div className="mt-3 flex items-center gap-4 text-sm text-zinc-500">
          <span>{post.is_author ? <span className="text-amber-500 inline-flex items-center gap-1"><ShieldCheck size={14} /> {post.author_name}</span> : post.author_name}</span>
          <span>{fmt(post.created_at)}</span>
          <span className="flex items-center gap-1.5"><Heart size={14} /> {post.likes}</span>
        </div>
        <div className="mt-6 text-zinc-200 leading-relaxed whitespace-pre-wrap text-lg">{post.body}</div>

        {/* replies */}
        <div className="mt-14">
          <h2 className="font-display text-2xl font-semibold mb-6">{replies.length} {replies.length === 1 ? "Reply" : "Replies"}</h2>
          <div className="space-y-5">
            {replies.map((r) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                data-testid={`reply-${r.id}`}
                className={`border-l-2 pl-5 py-1 ${r.is_author ? "border-amber-500" : "border-zinc-800"} ${r.hidden ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-3 text-sm mb-1">
                  <span className={r.is_author ? "text-amber-500 font-semibold inline-flex items-center gap-1" : "text-zinc-300"}>
                    {r.is_author && <ShieldCheck size={14} />}{r.author_name}{r.is_author && " · Author"}
                  </span>
                  <span className="text-zinc-600 text-xs">{fmt(r.created_at)}</span>
                  {r.hidden && <span className="text-[10px] tracking-accent uppercase text-red-400">Hidden</span>}
                </div>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-zinc-500">
                  <button data-testid={`like-reply-${r.id}`} onClick={() => onLike(r.id)} className={`flex items-center gap-1 transition-colors ${getLiked().includes(r.id) ? "text-amber-500" : "hover:text-amber-500"}`}>
                    <Heart size={13} fill={getLiked().includes(r.id) ? "currentColor" : "none"} /> {r.likes}
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => modReply(hideReply, r.id, r.hidden ? "Shown" : "Hidden")} className="inline-flex items-center gap-1 hover:text-amber-500">{r.hidden ? <><Eye size={12} /> Show</> : <><EyeOff size={12} /> Hide</>}</button>
                      <button onClick={() => { if (window.confirm("Delete this reply?")) modReply(deleteReply, r.id, "Deleted"); }} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 size={12} /> Delete</button>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <ReplyForm postId={post.id} isAdmin={isAdmin} onReplied={load} />
        </div>
      </section>
    </div>
  );
}

function ReplyForm({ postId, isAdmin, onReplied }) {
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [joinList, setJoinList] = useState(true);
  const [human, setHuman] = useState({ challenge_id: "", challenge_answer: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = isAdmin ? { body } : { name, email, body, join_list: joinList, ...human };
      await createReply(postId, payload);
      toast.success(isAdmin ? "Reply posted as Author." : "Reply posted!");
      setBody(""); setName(""); setEmail("");
      setRefreshKey((k) => k + 1);
      onReplied();
    } catch (err) {
      toast.error(apiError(err));
      setRefreshKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="reply-form" className="mt-10 border border-zinc-800 bg-zinc-900/40 p-6 space-y-4">
      <p className="text-xs tracking-accent uppercase text-amber-500">{isAdmin ? "Reply as Author" : "Join the conversation"}</p>
      {!isAdmin && (
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="reply-name" required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          <input data-testid="reply-email" required type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
        </div>
      )}
      <textarea data-testid="reply-body" required rows={3} placeholder="Write a reply…" value={body} onChange={(e) => setBody(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
      {!isAdmin && (
        <>
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={joinList} onChange={(e) => setJoinList(e.target.checked)} className="accent-amber-500" />
            Add me to the reader list
          </label>
          <HumanCheck value={human} onChange={setHuman} refreshKey={refreshKey} />
        </>
      )}
      <button data-testid="submit-reply" type="submit" disabled={loading} className="bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-6 py-3 hover:bg-amber-400 transition-colors disabled:opacity-60">
        {loading ? "Posting…" : "Post reply"}
      </button>
    </form>
  );
}
